<template>
  <div class="cloud-functions-container">
    <div class="cloud-functions-header">
      <h2>云函数管理</h2>
      <el-button v-if="$canWrite()" type="primary" @click="openCreateDialog">创建云函数</el-button>
    </div>
    
    <!-- 项目选择器 -->
    <el-card shadow="hover" class="project-selector-card" v-if="projects.length > 0">
      <div class="project-selector">
        <label>选择项目：</label>
        <el-select v-model="selectedProjectId" placeholder="请选择项目" style="width: 200px" @change="fetchFunctions">
          <el-option
            v-for="project in projects"
            :key="project.id"
            :label="project.name"
            :value="project.id"
          ></el-option>
        </el-select>
      </div>
    </el-card>
    
    <!-- 云函数列表 -->
    <el-card shadow="hover" class="cloud-functions-card" v-if="selectedProjectId">
      <el-table :data="paginatedFunctions" style="width: 100%" @selection-change="handleSelectionChange">
        <el-table-column v-if="$canWrite()" type="selection" width="55"></el-table-column>
        <el-table-column prop="id" label="ID" width="80"></el-table-column>
        <el-table-column prop="name" label="函数名称" width="200"></el-table-column>
        <el-table-column prop="runtime" label="运行时" width="120">
          <template slot-scope="scope">
            <el-tag :type="scope.row.runtime === 'php' ? 'success' : 'info'">
              {{ scope.row.runtime }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="handler" label="处理函数" width="100"></el-table-column>
        <el-table-column prop="status" label="状态" width="80">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'info'">
              {{ scope.row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" width=""></el-table-column>
        <el-table-column label="操作" width="280">
          <template slot-scope="scope">
            <el-button type="success" size="small" @click="viewLogs(scope.row)">日志</el-button>
            <el-button type="primary" size="small" @click="viewFunction(scope.row)">查看</el-button>
            <el-button v-if="$canWrite()" type="warning" size="small" @click="openEditDialog(scope.row)">编辑</el-button>
            <el-button v-if="$canWrite()" type="danger" size="small" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <!-- 分页 -->
      <div class="pagination-container">
        <div class="footer-left">
          <el-button
            v-if="$canWrite()"
            type="danger"
            size="small"
            :disabled="selectedFunctions.length === 0"
            @click="batchDelete"
          >
            批量删除 ({{ selectedFunctions.length }})
          </el-button>
        </div>
        
        <el-pagination
          @current-change="handlePageChange"
          :current-page="pagination.currentPage"
          :page-size="pagination.pageSize"
          layout="total, prev, pager, next"
          :total="totalFunctions"
        ></el-pagination>
      </div>
    </el-card>

    <!-- 创建云函数抽屉 -->
    <el-drawer
      title="创建云函数"
      :visible.sync="createDialogVisible"
      direction="rtl"
      size="50%"
      :before-close="handleDrawerClose"
    >
      <div class="drawer-content">
        <el-form ref="createForm" :model="form" :rules="rules" label-position="top">
          <el-form-item label="函数名称" prop="name">
            <el-input v-model="form.name" placeholder="请输入函数名称"></el-input>
          </el-form-item>
          <el-form-item label="运行时" prop="runtime">
            <el-select v-model="form.runtime" placeholder="请选择运行时" style="width: 100%">
              <el-option label="Node.js" value="nodejs"></el-option>
              <el-option label="PHP" value="php"></el-option>
              <el-option label="Python" value="python"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="处理函数" prop="handler">
            <el-input v-model="form.handler" placeholder="请输入处理函数名"></el-input>
          </el-form-item>
          <el-form-item label="函数代码" prop="code">
            <el-input 
              v-model="form.code" 
              placeholder="请输入函数代码" 
              type="textarea" 
              :rows="20"
              class="code-editor"
            ></el-input>
          </el-form-item>
          <el-form-item label="状态" prop="status">
            <el-radio-group v-model="form.status">
              <el-radio :label="1">启用</el-radio>
              <el-radio :label="0">禁用</el-radio>
            </el-radio-group>
          </el-form-item>
        </el-form>
        <div class="drawer-footer">
          <el-button @click="createDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleCreate" :loading="loading">创建</el-button>
        </div>
      </div>
    </el-drawer>

    <!-- 编辑云函数抽屉 -->
    <el-drawer
      title="编辑云函数"
      :visible.sync="editDialogVisible"
      direction="rtl"
      size="50%"
      :before-close="handleDrawerClose"
    >
      <div class="drawer-content">
        <el-form ref="editForm" :model="form" :rules="rules" label-position="top">
          <el-form-item label="函数名称" prop="name">
            <el-input v-model="form.name" placeholder="请输入函数名称"></el-input>
          </el-form-item>
          <el-form-item label="运行时" prop="runtime">
            <el-select v-model="form.runtime" placeholder="请选择运行时" style="width: 100%">
              <el-option label="Node.js" value="nodejs"></el-option>
              <el-option label="PHP" value="php"></el-option>
              <el-option label="Python" value="python"></el-option>
            </el-select>
          </el-form-item>
          <el-form-item label="处理函数" prop="handler">
            <el-input v-model="form.handler" placeholder="请输入处理函数名"></el-input>
          </el-form-item>
          <el-form-item label="函数代码" prop="code">
            <el-input 
              v-model="form.code" 
              placeholder="请输入函数代码" 
              type="textarea" 
              :rows="20"
              class="code-editor"
            ></el-input>
          </el-form-item>
          <el-form-item label="状态" prop="status">
            <el-radio-group v-model="form.status">
              <el-radio :label="1">启用</el-radio>
              <el-radio :label="0">禁用</el-radio>
            </el-radio-group>
          </el-form-item>
        </el-form>
        <div class="drawer-footer">
          <el-button @click="editDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleUpdate" :loading="loading">更新</el-button>
        </div>
      </div>
    </el-drawer>

    <!-- 查看日志抽屉 -->
    <el-drawer
      :title="`${currentFunction ? currentFunction.name : ''} - 调用日志`"
      :visible.sync="logsDrawerVisible"
      direction="rtl"
      size="60%"
    >
      <div class="drawer-content">
        <el-table :data="logs" style="width: 100%" max-height="calc(100vh - 200px)">
          <el-table-column prop="request_id" label="请求ID" width="180"></el-table-column>
          <el-table-column prop="status" label="状态" width="100">
            <template slot-scope="scope">
              <el-tag :type="scope.row.status === 'success' ? 'success' : 'danger'">
                {{ scope.row.status === 'success' ? '成功' : '失败' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="duration" label="耗时" width="100">
            <template slot-scope="scope">
              {{ scope.row.duration }}ms
            </template>
          </el-table-column>
          <el-table-column prop="ip_address" label="IP地址" width="150"></el-table-column>
          <el-table-column prop="created_at" label="调用时间" width="180"></el-table-column>
          <el-table-column label="操作" width="100">
            <template slot-scope="scope">
              <el-button type="text" size="small" @click="viewLogDetail(scope.row)">详情</el-button>
            </template>
          </el-table-column>
        </el-table>
        
        <el-pagination
          v-if="logsTotal > logsPageSize"
          @current-change="handleLogsPageChange"
          :current-page="logsPage"
          :page-size="logsPageSize"
          layout="total, prev, pager, next"
          :total="logsTotal"
          style="margin-top: 20px; text-align: center"
        ></el-pagination>
      </div>
    </el-drawer>

    <!-- 日志详情对话框 -->
    <el-dialog title="日志详情" :visible.sync="logDetailVisible" width="800px">
      <div v-if="currentLog">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="请求ID">{{ currentLog.request_id }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="currentLog.status === 'success' ? 'success' : 'danger'">
              {{ currentLog.status === 'success' ? '成功' : '失败' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="耗时">{{ currentLog.duration }}ms</el-descriptions-item>
          <el-descriptions-item label="IP地址">{{ currentLog.ip_address }}</el-descriptions-item>
          <el-descriptions-item label="调用时间" :span="2">{{ currentLog.created_at }}</el-descriptions-item>
        </el-descriptions>
        
        <el-divider>输入参数</el-divider>
        <pre class="json-display">{{ JSON.stringify(currentLog.input, null, 2) }}</pre>
        
        <el-divider v-if="currentLog.status === 'success'">输出结果</el-divider>
        <pre v-if="currentLog.status === 'success'" class="json-display">{{ JSON.stringify(currentLog.output, null, 2) }}</pre>
        
        <el-divider v-if="currentLog.status === 'error'">错误信息</el-divider>
        <el-alert v-if="currentLog.status === 'error'" :title="currentLog.error_message" type="error" :closable="false"></el-alert>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'CloudFunctionList',
  data() {
    return {
      projects: [],
      selectedProjectId: null,
      functions: [],
      selectedFunctions: [], // 选中的云函数
      // 分页
      pagination: {
        currentPage: 1,
        pageSize: 10
      },
      createDialogVisible: false,
      editDialogVisible: false,
      logsDrawerVisible: false,
      loading: false,
      currentFunction: null,
      logs: [],
      logsTotal: 0,
      logsPage: 1,
      logsPageSize: 20,
      logDetailVisible: false,
      currentLog: null,
      form: {
        id: null,
        name: '',
        runtime: 'nodejs', // 默认选中 Node.js
        handler: 'handler',
        code: '',
        status: 1
      },
      rules: {
        name: [
          { required: true, message: '请输入函数名称', trigger: 'blur' }
        ],
        runtime: [
          { required: true, message: '请选择运行时', trigger: 'change' }
        ],
        handler: [
          { required: true, message: '请输入处理函数名', trigger: 'blur' }
        ],
        code: [
          { required: true, message: '请输入函数代码', trigger: 'blur' }
        ]
      },
      // 代码模板
      codeTemplates: {
        nodejs: `/**
 * 云函数入口函数
 * @param {Object} event - 请求参数对象
 * @returns {Object} 返回结果
 */
async function handler(event) {
    // 在这里编写你的业务逻辑
    
    // 示例：返回接收到的参数
    return {
        message: 'Hello from Cloud Function!',
        receivedData: event,
        timestamp: new Date().toISOString()
    };
}`,
        php: `<?php
/**
 * 云函数入口函数
 * @param array $event 请求参数数组
 * @return array 返回结果
 */
function handler($event) {
    // 在这里编写你的业务逻辑
    
    // 示例：返回接收到的参数
    return [
        'message' => 'Hello from Cloud Function!',
        'receivedData' => $event,
        'timestamp' => date('Y-m-d H:i:s')
    ];
}

// 执行函数
return handler($payload ?? []);`,
        python: `"""
云函数入口函数
"""
import json
from datetime import datetime

def handler(event):
    """
    云函数入口函数
    :param event: 请求参数字典
    :return: 返回结果
    """
    # 在这里编写你的业务逻辑
    
    # 示例：返回接收到的参数
    return {
        'message': 'Hello from Cloud Function!',
        'receivedData': event,
        'timestamp': datetime.now().isoformat()
    }`
      }
    }
  },
  computed: {
    paginatedFunctions() {
      const start = (this.pagination.currentPage - 1) * this.pagination.pageSize;
      const end = start + this.pagination.pageSize;
      return this.functions.slice(start, end);
    },
    totalFunctions() {
      return this.functions.length;
    }
  },
  created() {
    const spaceId = this.$route.params.id;
    if (spaceId) {
      this.selectedProjectId = spaceId;
      this.fetchFunctions();
    }
  },
  watch: {
    // 监听运行时变化，自动更新代码模板
    'form.runtime'(newRuntime) {
      // 只在创建对话框打开且代码为空或为模板代码时自动更新
      if (this.createDialogVisible && this.isTemplateCode(this.form.code)) {
        this.form.code = this.codeTemplates[newRuntime] || '';
      }
    }
  },
  methods: {
    // 页码改变
    handlePageChange(page) {
      this.pagination.currentPage = page;
    },
    
    // 判断是否是模板代码
    isTemplateCode(code) {
      if (!code) return true;
      // 检查是否是任一模板代码
      return Object.values(this.codeTemplates).some(template => code.trim() === template.trim());
    },
    handleDrawerClose(done) {
      // 关闭抽屉前的确认
      done();
    },
    async fetchProjects() {
      try {
        const projects = await this.$store.dispatch('fetchProjects')
        this.projects = projects
        if (projects.length > 0) {
          this.selectedProjectId = projects[0].id
          this.fetchFunctions()
        } else {
          this.$message.warning('暂无项目，请先创建项目')
        }
      } catch (error) {
        console.error('获取项目列表失败:', error)
        this.$message.error('获取项目列表失败: ' + (error.response?.data?.error || error.message))
      }
    },
    async fetchFunctions() {
      if (!this.selectedProjectId) return
      
      try {
        const response = await this.$axios.get(`/projects/${this.selectedProjectId}/functions`)
        this.functions = response.data || []
      } catch (error) {
        console.error('获取云函数列表失败:', error)
        this.$message.error('获取云函数列表失败: ' + (error.response?.data?.error || error.message))
      }
    },
    openCreateDialog() {
      this.form = {
        id: null,
        name: '',
        runtime: 'nodejs', // 默认 Node.js
        handler: 'handler',
        code: this.codeTemplates.nodejs, // 自动填充 Node.js 模板
        status: 1
      }
      this.createDialogVisible = true
    },
    openEditDialog(func) {
      this.form = JSON.parse(JSON.stringify(func))
      this.editDialogVisible = true
    },
    async handleCreate() {
      this.$refs.createForm.validate(async (valid) => {
        if (valid) {
          this.loading = true
          try {
            await this.$axios.post(`/projects/${this.selectedProjectId}/functions`, this.form)
            this.$message.success('云函数创建成功')
            this.createDialogVisible = false
            this.fetchFunctions()
          } catch (error) {
            this.$message.error('云函数创建失败')
          } finally {
            this.loading = false
          }
        }
      })
    },
    async handleUpdate() {
      this.$refs.editForm.validate(async (valid) => {
        if (valid) {
          this.loading = true
          try {
            await this.$axios.put(`/projects/${this.selectedProjectId}/functions/${this.form.id}`, this.form)
            this.$message.success('云函数更新成功')
            this.editDialogVisible = false
            this.fetchFunctions()
          } catch (error) {
            this.$message.error('云函数更新失败')
          } finally {
            this.loading = false
          }
        }
      })
    },
    async handleDelete(func) {
      this.$confirm('确定要删除这个云函数吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await this.$axios.delete(`/projects/${this.selectedProjectId}/functions/${func.id}`)
          this.$message.success('云函数删除成功')
          this.fetchFunctions()
        } catch (error) {
          this.$message.error('云函数删除失败')
        }
      }).catch(() => {
        this.$message.info('已取消删除')
      })
    },
    viewFunction() {
      this.$message.info('查看云函数详情功能开发中')
    },
    async viewLogs(func) {
      this.currentFunction = func
      this.logsDrawerVisible = true
      this.logsPage = 1
      await this.fetchLogs()
    },
    async fetchLogs() {
      if (!this.currentFunction) return
      
      try {
        const response = await this.$axios.get(
          `/projects/${this.selectedProjectId}/functions/${this.currentFunction.id}/logs`,
          {
            params: {
              page: this.logsPage,
              page_size: this.logsPageSize
            }
          }
        )
        this.logs = response.data.data || []
        this.logsTotal = response.data.total || 0
      } catch (error) {
        console.error('获取日志失败:', error)
        this.$message.error('获取日志失败: ' + (error.response?.data?.error || error.message))
      }
    },
    handleLogsPageChange(page) {
      this.logsPage = page
      this.fetchLogs()
    },
    viewLogDetail(log) {
      this.currentLog = log
      this.logDetailVisible = true
    },
    
    // 处理表格多选
    handleSelectionChange(selection) {
      this.selectedFunctions = selection;
    },
    
    // 批量删除云函数
    async batchDelete() {
      if (this.selectedFunctions.length === 0) {
        this.$message.warning('请先选择要删除的云函数');
        return;
      }

      try {
        await this.$confirm(`确定要删除选中的 ${this.selectedFunctions.length} 个云函数吗？此操作不可恢复！`, '警告', {
          type: 'warning',
          confirmButtonText: '确定删除',
          cancelButtonText: '取消'
        });
        
        const ids = this.selectedFunctions.map(func => func.id);
        
        await this.$axios.post(`/projects/${this.selectedProjectId}/functions/batch-delete`, {
          ids: ids
        });
        
        this.$message.success(`成功删除 ${ids.length} 个云函数`);
        this.selectedFunctions = [];
        this.fetchFunctions();
      } catch (error) {
        if (error !== 'cancel') {
          console.error('批量删除失败:', error);
          this.$message.error('批量删除失败: ' + (error.response?.data?.error || error.message));
        }
      }
    }
  }
}
</script>

<style scoped>
.cloud-functions-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.cloud-functions-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-shrink: 0;
}

.cloud-functions-header h2 {
  margin: 0;
  color: #303133;
}

.project-selector-card {
  margin-bottom: 20px;
  flex-shrink: 0;
}

.project-selector {
  display: flex;
  align-items: center;
  gap: 10px;
}

.project-selector label {
  font-weight: 500;
  color: #606266;
}

.cloud-functions-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.cloud-functions-card >>> .el-card__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
}

.cloud-functions-card >>> .el-table {
  flex: 1;
  overflow-y: auto;
}

/* 分页固定在底部 */
.pagination-container {
  padding: 16px;
  border-top: 1px solid #e4e7ed;
  background: white;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-shrink: 0;
  gap: 16px;
}

.footer-left {
  margin-right: auto;
}

/* 抽屉内容样式 */
.drawer-content {
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.drawer-content .el-form {
  flex: 1;
  overflow-y: auto;
}

.drawer-footer {
  padding: 20px 0;
  text-align: right;
  border-top: 1px solid #e4e7ed;
}

/* 代码编辑器样式 */
.code-editor >>> textarea {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace;
  font-size: 13px;
  line-height: 1.6;
}

/* JSON 显示样式 */
.json-display {
  background-color: #f5f7fa;
  padding: 15px;
  border-radius: 4px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace;
  font-size: 13px;
  line-height: 1.6;
  overflow-x: auto;
  max-height: 400px;
  overflow-y: auto;
}
</style>
